<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SupportTicketStatus } from '@/types/support'
import type { SupportTicket } from '@/types/support'
import SupportStatusBadge from './SupportStatusBadge.vue'
import SupportMessageBubble from './SupportMessageBubble.vue'
import SupportStatusMarker from './SupportStatusMarker.vue'
import MarkdownEditor from '@/components/common/MarkdownEditor.vue'
import MultiFileDropZone from '@/components/common/MultiFileDropZone.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import { useSupportAttachments } from '@/composables/useSupportAttachments'

const props = defineProps<{
  ticket: SupportTicket
  submitting: boolean
  organisationId: number
}>()

const emit = defineEmits<{
  reply: [payload: { body: string; attachmentIds: number[] }]
  close: []
  reopen: []
}>()

const { t } = useI18n()

const isClosed = computed(() => props.ticket.status === SupportTicketStatus.Closed)

// Reply composer state
const replyBody = ref('')
const replyFiles = ref<File[]>([])

const { uploads, attachmentIds, upload, remove, reset: resetAttachments } = useSupportAttachments(props.organisationId)

const hasUploadingFiles = computed(() => uploads.value.some((u) => u.status === 'uploading'))

async function handleFileAdd(file: File): Promise<void> {
  await upload(file)
}

function handleFileRemove(tempId: string): void {
  remove(tempId)
}

function handleSendReply(): void {
  if (!replyBody.value.trim() || props.submitting || hasUploadingFiles.value) return
  emit('reply', { body: replyBody.value.trim(), attachmentIds: attachmentIds.value })
  replyBody.value = ''
  replyFiles.value = []
  resetAttachments()
}

// Status markers (v1: only closed_at marker)
const statusMarkers = computed(() => {
  if (!props.ticket.closed_at) return []
  return [
    {
      at: props.ticket.closed_at,
      label: t('support.thread.status_marker', { status: t('support.status.closed') }),
    },
  ]
})

// Format dates for the metadata rail
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function relativeTime(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diff = Math.floor((now - then) / 1000)
  if (diff < 60) return t('support.relative_time.seconds_ago', { n: diff })
  if (diff < 3600) return t('support.relative_time.minutes_ago', { n: Math.floor(diff / 60) })
  if (diff < 86400) return t('support.relative_time.hours_ago', { n: Math.floor(diff / 3600) })
  if (diff < 86400 * 2) return t('support.relative_time.yesterday')
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
</script>

<template>
  <div class="flex flex-col lg:flex-row gap-6">
    <!-- Metadata rail -->
    <aside class="w-full lg:w-56 shrink-0">
      <div class="rounded-xl bg-slate-800/50 border border-slate-700 p-4 space-y-4">
        <!-- Status -->
        <div>
          <p class="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
            {{ t('support.thread.rail_status') }}
          </p>
          <SupportStatusBadge :status="ticket.status" />
        </div>

        <!-- Category -->
        <div>
          <p class="text-xs uppercase tracking-wider text-slate-400 mb-1">
            {{ t('support.thread.rail_category') }}
          </p>
          <p class="text-sm text-slate-300">{{ t(`support.category.${ticket.category}`) }}</p>
        </div>

        <!-- Opened -->
        <div>
          <p class="text-xs uppercase tracking-wider text-slate-400 mb-1">
            {{ t('support.thread.rail_opened') }}
          </p>
          <p class="text-sm text-slate-300">{{ formatDate(ticket.created_at) }}</p>
        </div>

        <!-- Last activity -->
        <div>
          <p class="text-xs uppercase tracking-wider text-slate-400 mb-1">
            {{ t('support.thread.rail_last_activity') }}
          </p>
          <p class="text-sm text-slate-300">{{ relativeTime(ticket.last_activity_at) }}</p>
        </div>

        <!-- Divider + action -->
        <hr class="border-slate-700" />

        <button
          v-if="!isClosed"
          type="button"
          class="w-full text-left text-sm font-medium text-slate-400 hover:text-white transition-colors py-1"
          @click="emit('close')"
        >
          {{ t('support.thread.close') }}
        </button>

        <button
          v-else
          type="button"
          class="w-full text-left text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors py-1"
          @click="emit('reopen')"
        >
          {{ t('support.thread.reopen') }}
        </button>
      </div>
    </aside>

    <!-- Conversation + composer -->
    <div class="flex-1 flex flex-col gap-4 min-w-0">
      <!-- Messages timeline -->
      <div class="flex flex-col gap-4">
        <template v-for="message in ticket.messages ?? []" :key="message.id">
          <!-- Insert closed marker before the last message if ticket is closed and marker timestamp falls between messages -->
          <template v-for="marker in statusMarkers" :key="`marker-${marker.at}`">
            <SupportStatusMarker
              v-if="message === (ticket.messages ?? [])[ticket.messages!.length - 1] && isClosed"
              :label="marker.label"
              :at="marker.at"
            />
          </template>
          <SupportMessageBubble :message="message" />
        </template>
      </div>

      <!-- Closed banner -->
      <div
        v-if="isClosed"
        class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 flex items-center justify-between gap-4"
      >
        <div>
          <p class="text-sm font-semibold text-white">{{ t('support.thread.closed_banner_title') }}</p>
          <p class="text-xs text-slate-400 mt-0.5">{{ t('support.thread.closed_banner_body') }}</p>
        </div>
        <button
          type="button"
          :disabled="submitting"
          class="shrink-0 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="emit('reopen')"
        >
          <AppSpinner v-if="submitting" size="button" />
          {{ t('support.thread.reopen') }}
        </button>
      </div>

      <!-- Reply composer (hidden when closed) -->
      <div
        v-if="!isClosed"
        class="rounded-xl bg-slate-800/50 border border-slate-700 p-4 space-y-4"
      >
        <MarkdownEditor
          v-model="replyBody"
          :placeholder="t('support.thread.reply_placeholder')"
          :max-length="50000"
        />

        <MultiFileDropZone
          v-model="replyFiles"
          :uploads="uploads"
          :max-size-mb="10"
          accepted-types="image/png,image/jpeg,application/pdf"
          :max-files="10"
          @add="handleFileAdd"
          @remove="handleFileRemove"
        />

        <div class="flex justify-end">
          <button
            type="button"
            :disabled="submitting || !replyBody.trim() || hasUploadingFiles"
            class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="handleSendReply"
          >
            <svg v-if="submitting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ t('support.thread.send_reply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
